"use client";

import { useState } from "react";
import { ArrowRight, Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Inquiries() {
  const categories = [{
    id: "automation",
    label: "Automation"
  }, {
    id: "integration",
    label: "Integration"
  }, {
    id: "pricing",
    label: "Pricing"
  }, {
    id: "support",
    label: "Support"
  }, {
    id: "security",
    label: "Security"
  }];
  const faqsByCategory = [{
    categoryId: "automation",
    question: "How does AI automation streamline my business?",
    answer: "AI automation removes repetitive manual tasks by handling data entry, scheduling, and customer responses instantly. This allows your team to focus on high-value strategy while our systems ensure 24/7 operational efficiency."
  }, {
    categoryId: "automation",
    question: "Can you automate my existing customer support?",
    answer: "Yes. We deploy intelligent AI chatbots that resolve common inquiries, qualify leads, and escalate complex issues to your human staff, significantly reducing response times and operational overhead."
  }, {
    categoryId: "automation",
    question: "What types of workflows can be automated?",
    answer: "We specialize in appointment booking, invoicing, email marketing sequences, lead nurturing, and cross-platform data synchronization. If it involves digital inputs and outputs, we can likely automate it."
  }, {
    categoryId: "integration",
    question: "Will your solutions work with my current software?",
    answer: "Our automation frameworks are designed to integrate seamlessly with most CRM, ERP, and communication platforms. We use robust API connections to ensure your existing tech stack remains fully functional."
  }, {
    categoryId: "integration",
    question: "Do I need technical expertise to manage these systems?",
    answer: "Not at all. We build intuitive dashboards and automated triggers that require zero coding knowledge. We provide full training and documentation to ensure your team is comfortable with the new workflows."
  }, {
    categoryId: "integration",
    question: "Can you handle custom API integrations?",
    answer: "Absolutely. Our engineering team builds bespoke middleware to connect proprietary or legacy systems, ensuring your data flows securely and accurately across your entire digital ecosystem."
  }, {
    categoryId: "pricing",
    question: "How is the pricing for automation services structured?",
    answer: "We offer transparent, project-based pricing or monthly retainers depending on the complexity of your automation needs. We focus on delivering measurable ROI through time and cost savings."
  }, {
    categoryId: "pricing",
    question: "Are there hidden costs for maintenance?",
    answer: "We believe in total transparency. All maintenance, updates, and monitoring are included in our service agreements, so you never face unexpected technical debt or surprise fees."
  }, {
    categoryId: "pricing",
    question: "Do you offer scalable plans for growing businesses?",
    answer: "Yes. Our modular architecture allows you to start with a single high-impact automation and scale up as your business grows, ensuring your investment always aligns with your current operational scale."
  }, {
    categoryId: "support",
    question: "What kind of ongoing support do you provide?",
    answer: "We provide dedicated account management, 24/7 system monitoring, and rapid response troubleshooting. Our goal is to ensure your automated systems run flawlessly around the clock."
  }, {
    categoryId: "support",
    question: "How quickly can you implement a new automation?",
    answer: "Implementation timelines vary by complexity, but most core automations are deployed and tested within 2 to 4 weeks. We prioritize speed-to-value for every client project."
  }, {
    categoryId: "support",
    question: "Do you provide training for my staff?",
    answer: "Comprehensive training is a core part of our delivery. We conduct workshops and provide video guides to ensure your team understands how to leverage the new AI tools effectively."
  }, {
    categoryId: "security",
    question: "How do you ensure my business data is secure?",
    answer: "Security is our top priority. We implement enterprise-grade encryption, strict access controls, and compliant data handling practices to ensure your proprietary information remains protected at all times."
  }, {
    categoryId: "security",
    question: "Are your AI solutions GDPR and privacy compliant?",
    answer: "Yes. We adhere to strict data privacy standards and ensure all automated workflows are configured to meet regional regulatory requirements, including GDPR and local privacy laws."
  }, {
    categoryId: "security",
    question: "Where is my data stored?",
    answer: "We prioritize data sovereignty and work with you to select hosting environments that meet your specific compliance needs, whether that is local cloud infrastructure or secure private servers."
  }];
  const [activeCategory, setActiveCategory] = useState("automation");
  const filteredFaqs = faqsByCategory.filter(faq => faq.categoryId === activeCategory);
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="497" id="common-questions" className="bg-background py-20 md:py-32">
      <div className="container mx-auto px-6 max-w-7xl">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 md:mb-24 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs tracking-[0.2em] uppercase mb-6 !text-[rgba(255,255,255,1)]">
            § 0X Inquiries
          </p>
          <h2 className="font-serif font-default text-4xl md:text-5xl lg:text-[52px] leading-[1.1] text-foreground mb-6">
            Decoding <span className="!text-[rgba(255,255,255,1)]">Automation</span>
          </h2>
          <p className="font-default text-base md:text-lg text-foreground/70 leading-relaxed">
            Answers to the most common technical and operational questions regarding our AI integration services.
          </p>
        </div>

        <div ref={contentRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 transition-all duration-700 delay-150 ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-foreground/40 mb-6 hidden lg:block">
                Categories
              </p>
              <ul className="flex flex-row lg:flex-col gap-1 overflow-x-auto lg:overflow-visible -mx-6 px-6 lg:mx-0 lg:px-0 pb-2 lg:pb-0">
                {categories.map((category, i) => {
                const isActive = activeCategory === category.id;
                return <li key={category.id} data-index={i} className="shrink-0 lg:shrink">
                      <button onClick={() => setActiveCategory(category.id)} className={`relative font-mono text-xs tracking-[0.2em] uppercase py-3 pl-4 pr-4 transition-colors text-left w-full ${isActive ? "text-foreground" : "text-foreground/50 hover:text-foreground/80"}`}>
                        <span className={`absolute left-0 top-1/2 -translate-y-1/2 w-[2px] transition-all duration-300 ${isActive ? "h-6 bg-[var(--chart-1)]" : "h-0 bg-transparent"}`} aria-hidden="true" />
                        {category.label}
                      </button>
                    </li>;
              })}
              </ul>
            </div>
          </aside>

          {}
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="w-full" key={activeCategory} defaultValue="item-0">
              {filteredFaqs.map((faq, i) => <AccordionItem key={`${activeCategory}-${i}`} data-index={i} value={`item-${i}`} className="border-b-0 border-t border-border first:border-t last:border-b group">
                  <AccordionTrigger className="hover:no-underline py-6 [&>svg]:hidden group">
                    <span className="font-serif font-default text-lg md:text-xl text-foreground leading-snug pr-6">
                      {faq.question}
                    </span>
                    <Plus className="size-5 shrink-0 text-foreground/70 transition-transform duration-300 group-data-[state=open]:rotate-45" aria-hidden="true" />
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 pr-10">
                    <p className="font-default text-base text-foreground/80 leading-[1.7]">
                      {faq.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>

            {}
            <div className="mt-12 bg-[var(--chart-2)] rounded-lg p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--void-black)]/70 mb-2">
                  Still curious?
                </p>
                <h3 className="font-serif font-default text-2xl md:text-[28px] text-[var(--void-black)] leading-tight">
                  Need a custom solution? Contact us.
                </h3>
              </div>
              <Link to="/ConnectPage" className="inline-flex items-center gap-3 font-mono text-xs tracking-[0.2em] uppercase text-[var(--void-black)] hover:gap-4 transition-all shrink-0 group/link">
                <span>Get in touch</span>
                <ArrowRight className="size-4 transition-transform group-hover/link:translate-x-1" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}